import React, { useState, useEffect } from 'react';
import { Checkbox, Alert, Spin, Collapse, Typography as AntTypography } from 'antd';
import { FileOutlined, WarningOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { colors, spacing } from '../design-system/tokens';
import { Typography } from '../design-system/typography';

const { Panel } = Collapse;
const { Text } = AntTypography;

export type DocumentType =
  | 'contract_buyer'
  | 'contract_seller'
  | 'shipping_instruction'
  | 'coo'
  | 'invoice'
  | 'packing_list'
  | 'booking_request';

interface Document {
  id: string;
  documentType: string;
  originalName: string;
  mimeType: string;
  size: number;
  createdAt: string;
}

interface DocumentSelectorProps {
  entityType: 'shipment' | 'contract';
  entityId: string;
  allowedGenerateTypes: DocumentType[];
  onSelectionChange: (selection: {
    generate: DocumentType[];
    attach: string[];
  }) => void;
  defaultSelection?: {
    generate?: DocumentType[];
    attach?: string[];
  };
}

const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  contract_buyer: 'Buyer Contract',
  contract_seller: 'Seller Contract',
  shipping_instruction: 'Shipping Instruction',
  coo: 'Certificate of Origin',
  invoice: 'Commercial Invoice',
  packing_list: 'Packing List',
  booking_request: 'Booking Request'
};

const GMAIL_SIZE_LIMIT_MB = 25;
const WARNING_THRESHOLD_MB = 20; // Warn at 20MB (80% of limit)

export const DocumentSelector: React.FC<DocumentSelectorProps> = ({
  entityType,
  entityId,
  allowedGenerateTypes,
  onSelectionChange,
  defaultSelection
}) => {
  const [selectedGenerate, setSelectedGenerate] = useState<DocumentType[]>(defaultSelection?.generate || []);
  const [selectedAttach, setSelectedAttach] = useState<string[]>(defaultSelection?.attach || []);
  const [estimatedSizeMB, setEstimatedSizeMB] = useState<number>(0);

  // Fetch available documents
  const { data: documentsData, isLoading, error } = useQuery({
    queryKey: ['available-documents', entityType, entityId],
    queryFn: async () => {
      // Use authenticated API client
      const response = await api.get(
        `/email-drafts/available-documents?entityType=${entityType}&entityId=${entityId}`
      );
      return response.data;
    },
    enabled: !!entityType && !!entityId
  });

  const documents: Document[] = documentsData?.documents || [];

  // Calculate estimated total size
  useEffect(() => {
    let totalSize = 0;

    // Estimate ~500KB per generated PDF
    totalSize += selectedGenerate.length * 500 * 1024;

    // Add actual size of existing documents
    selectedAttach.forEach(docId => {
      const doc = documents.find(d => d.id === docId);
      if (doc) {
        totalSize += doc.size;
      }
    });

    const sizeMB = totalSize / (1024 * 1024);
    setEstimatedSizeMB(sizeMB);
  }, [selectedGenerate, selectedAttach, documents]);

  // Notify parent of selection changes
  useEffect(() => {
    onSelectionChange({
      generate: selectedGenerate,
      attach: selectedAttach
    });
  }, [selectedGenerate, selectedAttach, onSelectionChange]);

  const handleGenerateChange = (type: DocumentType, checked: boolean) => {
    setSelectedGenerate(prev =>
      checked ? [...prev, type] : prev.filter(t => t !== type)
    );
  };

  const handleAttachChange = (docId: string, checked: boolean) => {
    setSelectedAttach(prev =>
      checked ? [...prev, docId] : prev.filter(id => id !== docId)
    );
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getSizeWarning = (): { type: 'warning' | 'error'; message: string } | null => {
    if (estimatedSizeMB >= GMAIL_SIZE_LIMIT_MB) {
      return {
        type: 'error',
        message: `Attachment size (${estimatedSizeMB.toFixed(2)}MB) exceeds Gmail's 25MB limit. Please reduce the number of attachments.`
      };
    }
    if (estimatedSizeMB >= WARNING_THRESHOLD_MB) {
      return {
        type: 'warning',
        message: `Attachment size (${estimatedSizeMB.toFixed(2)}MB) is approaching Gmail's 25MB limit. Consider reducing attachments.`
      };
    }
    return null;
  };

  const sizeWarning = getSizeWarning();

  return (
    <div style={{ marginBottom: spacing.lg }}>
      {/* Size Warning */}
      {sizeWarning && (
        <Alert
          type={sizeWarning.type}
          message={sizeWarning.message}
          icon={<WarningOutlined />}
          showIcon
          style={{ marginBottom: spacing.md }}
        />
      )}

      {/* Estimated Total Size */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: spacing.md,
          padding: spacing.sm,
          backgroundColor: colors.background.secondary,
          borderRadius: '4px'
        }}
      >
        <Typography variant="label" as="span">
          ESTIMATED TOTAL SIZE:
        </Typography>
        <Text
          strong
          style={{
            color:
              estimatedSizeMB >= GMAIL_SIZE_LIMIT_MB
                ? colors.status.negative
                : estimatedSizeMB >= WARNING_THRESHOLD_MB
                ? colors.status.warning
                : colors.text.primary
          }}
        >
          {estimatedSizeMB.toFixed(2)} MB / 25 MB
        </Text>
      </div>

      {/* Two-Column Layout */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: spacing.lg
        }}
      >
        {/* Generate New Documents */}
        <div>
          <Typography
            variant="label"
            as="div"
            style={{
              color: colors.text.secondary,
              marginBottom: spacing.sm,
              fontSize: '14px'
            }}
          >
            GENERATE NEW DOCUMENTS
          </Typography>

          {allowedGenerateTypes.length === 0 ? (
            <div style={{ color: colors.text.tertiary, fontStyle: 'italic' }}>
              No document types available for generation
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
              {allowedGenerateTypes.map(type => (
                <Checkbox
                  key={type}
                  checked={selectedGenerate.includes(type)}
                  onChange={e => handleGenerateChange(type, e.target.checked)}
                  disabled={type === 'booking_request'} // Booking requests don't generate PDFs
                >
                  <span style={{ fontSize: '13px' }}>
                    {DOCUMENT_TYPE_LABELS[type]}
                    {type === 'booking_request' && (
                      <span style={{ color: colors.text.tertiary, marginLeft: spacing.xs }}>
                        (Email only)
                      </span>
                    )}
                  </span>
                </Checkbox>
              ))}
            </div>
          )}

          <div
            style={{
              marginTop: spacing.sm,
              fontSize: '11px',
              color: colors.text.tertiary
            }}
          >
            {selectedGenerate.length === 0
              ? 'No documents selected for generation'
              : `${selectedGenerate.length} document${selectedGenerate.length !== 1 ? 's' : ''} will be generated (~${(selectedGenerate.length * 0.5).toFixed(1)} MB)`}
          </div>
        </div>

        {/* Attach Existing Documents */}
        <div>
          <Typography
            variant="label"
            as="div"
            style={{
              color: colors.text.secondary,
              marginBottom: spacing.sm,
              fontSize: '14px'
            }}
          >
            ATTACH EXISTING DOCUMENTS
          </Typography>

          {isLoading ? (
            <div style={{ textAlign: 'center', padding: spacing.md }}>
              <Spin size="small" />
              <div style={{ marginTop: spacing.xs, fontSize: '12px', color: colors.text.tertiary }}>
                Loading documents...
              </div>
            </div>
          ) : error ? (
            <Alert
              type="error"
              message="Failed to load documents"
              description="Please try again later"
              showIcon
              style={{ fontSize: '12px' }}
            />
          ) : documents.length === 0 ? (
            <div style={{ color: colors.text.tertiary, fontStyle: 'italic', fontSize: '13px' }}>
              No existing documents available
            </div>
          ) : (
            <Collapse
              ghost
              defaultActiveKey={[]}
              style={{ maxHeight: '300px', overflowY: 'auto' }}
            >
              {Object.entries(documentsData?.grouped || {}).map(([docType, docs]: [string, any]) => (
                <Panel
                  header={
                    <span style={{ fontSize: '13px', fontWeight: 500 }}>
                      {docType.replace(/_/g, ' ')} ({docs.length})
                    </span>
                  }
                  key={docType}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                    {docs.map((doc: Document) => (
                      <Checkbox
                        key={doc.id}
                        checked={selectedAttach.includes(doc.id)}
                        onChange={e => handleAttachChange(doc.id, e.target.checked)}
                      >
                        <div style={{ fontSize: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
                            <FileOutlined style={{ color: colors.text.tertiary }} />
                            <span>{doc.originalName}</span>
                          </div>
                          <div style={{ color: colors.text.tertiary, fontSize: '11px' }}>
                            {formatFileSize(doc.size)} • {formatDate(doc.createdAt)}
                          </div>
                        </div>
                      </Checkbox>
                    ))}
                  </div>
                </Panel>
              ))}
            </Collapse>
          )}

          <div
            style={{
              marginTop: spacing.sm,
              fontSize: '11px',
              color: colors.text.tertiary
            }}
          >
            {selectedAttach.length === 0
              ? 'No existing documents selected'
              : `${selectedAttach.length} existing document${selectedAttach.length !== 1 ? 's' : ''} selected`}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentSelector;
